import type { FieldValues } from "react-hook-form";

import { toMonth } from "../providers/commons/financeSchedule";
import type { FinanceCharge, FinanceContract } from "../types";
import { dayToTimestamp, toNullableNumber, toNullableText } from "./formValues";

/** Values written to `finance_charges` from the charge form */
export const toChargeData = (
  values: FieldValues,
  previous?: FinanceCharge | null,
): Partial<FinanceCharge> => ({
  company_id: values.company_id,
  contract_id: values.contract_id || null,
  kind: values.kind,
  description: toNullableText(values.description),
  amount: Number(values.amount),
  competence_month: toMonth(values.competence_month),
  due_date: values.due_date,
  sent_at: dayToTimestamp(values.sent_on, previous?.sent_at),
  paid_at: dayToTimestamp(values.paid_on, previous?.paid_at),
  paid_amount: values.paid_on ? toNullableNumber(values.paid_amount) : null,
  payment_method: values.payment_method || null,
  invoice_number: toNullableText(values.invoice_number),
  invoice_files: values.invoice_files ?? [],
  receipt_files: values.receipt_files ?? [],
  notes: toNullableText(values.notes),
});

/** Values written to `finance_contracts` from the contract form */
export const toContractData = (
  values: FieldValues,
  previous?: FinanceContract | null,
  now: Date = new Date(),
): Partial<FinanceContract> => ({
  name: String(values.name).trim(),
  company_id: values.company_id,
  deal_id: values.deal_id || null,
  status: values.status,
  setup_amount: Number(values.setup_amount),
  setup_installments: Number(values.setup_installments),
  monthly_amount: Number(values.monthly_amount),
  billing_day: Number(values.billing_day),
  start_date: values.start_date,
  end_date: values.end_date || null,
  // Kept while the contract stays cancelled, so the churn keeps its date
  cancelled_at:
    values.status === "cancelled"
      ? (previous?.cancelled_at ?? now.toISOString())
      : null,
  adjustment_index: values.adjustment_index,
  adjustment_month:
    values.adjustment_index === "none" ? null : Number(values.adjustment_month),
  notes: toNullableText(values.notes),
});
